import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import AddEditDynamicIvrModal from './AddEditDynamicIvrModal';
import {
  dynamicIvrDelete,
  dynamicIvrListGet,
  useDynamicIvrList
} from '../../../store/slices/tenantSlices/incomingSlice';
import { crmsOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const DynamicIvr = () => {
  const dispatch = useDispatch();
  const dynamicIvrList = useDynamicIvrList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [crmOptions, setCrmOptions] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined,
    crm_id: undefined
  });

  const fetchDynamicIvrList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(dynamicIvrListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setCrmOptions([{ label: 'All CRM', value: '' }, ...options]);
      } else {
        setCrmOptions([{ label: 'All CRM', value: '' }]);
      }
    } catch (error) {
      setCrmOptions([{ label: 'All CRM', value: '' }]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getCrmsOption();
  }, []);

  const deleteDynamicIvr = async () => {
    try {
      const response = await dispatch(dynamicIvrDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (dynamicIvrList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchDynamicIvrList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchDynamicIvrList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Dynamic IVRs"
        isExpandSearch
        isAdd
        isAddDisabled={!checkModulePermission('is_add', 'Dynamic IVRs', userRolePermission)}
        onAddClick={() => setOpenAddModal(true)}
        addBtnName="Add Dynamic IVR"
        searchPlaceholder="Search Dynamic IVRs"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
        isExpandSelect
        options={crmOptions}
        selectPlaceholder="Filter by CRM"
        selectValue={filter.crm_id || ''}
        handelSelect={(e) => setFilter({ ...filter, page: 1, crm_id: e.target.value || undefined })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : dynamicIvrList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Dynamic IVRs', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>IVR Name</th>
                  <th>Script</th>
                  <th>Mapping Fields</th>
                  <th>CRM</th>
                  <th>DTMF Options</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {dynamicIvrList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.ivr_name}</td>
                      <td>{val?.script}</td>
                      <td>{Object.keys(val?.mapping_fields || {}).length}</td>
                      <td>
                        {crmOptions.find((opt) => opt.value === String(val?.crm_id))?.label ||
                          val?.crm_name ||
                          val?.crm_id}
                      </td>
                      <td>{Number(val?.dtmf_count || 0)}</td>
                      <td>{val?.status === false ? 'Inactive' : 'Active'}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Dynamic IVRs', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_edit',
                                    'Dynamic IVRs',
                                    userRolePermission
                                  )
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Dynamic IVRs', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    'Dynamic IVRs',
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={dynamicIvrList?.total}
              limit={dynamicIvrList?.limit}
              page={dynamicIvrList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchDynamicIvrList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteDynamicIvr()}
      />
      <AddEditDynamicIvrModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchDynamicIvrList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default DynamicIvr;
